import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TablePagination,
  TableSortLabel,
  CircularProgress,
  Button,
  Stack,
  Card,
  CardContent,
  TextField,
  InputAdornment,
  Checkbox,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Grid,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import {
  Search as SearchIcon,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
  Sync as SyncIcon,
  Cancel as CancelIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import tesoreriaService, {
  ChequePropio,
  ChequePropioEstadoFiltro,
  type ChequesSortDir,
} from '../../services/tesoreriaService';
import {
  loadTesoreriaFiltros,
  storedNumber,
  storedString,
  TESORERIA_FILTROS_KEYS,
  usePersistTesoreriaFiltros,
} from '../../utils/tesoreriaFiltrosStorage';
import { formatNroChequeVista } from '../../utils/nroCheque';

type ChequePropioSortKey =
  | 'nombreBanco'
  | 'nroCuenta'
  | 'destino'
  | 'librador'
  | 'nroBanco'
  | 'nroSucursal'
  | 'nroCheque'
  | 'fechaVencimiento'
  | 'fechaAcred'
  | 'importe';

interface ChequesPropiosPendientesFiltrosStored {
  searchTerm?: string;
  rowsPerPage?: number;
  estado?: ChequePropioEstadoFiltro;
  fechaVencimientoDesde?: string;
  fechaVencimientoHasta?: string;
}

const ESTADOS: { value: ChequePropioEstadoFiltro; label: string }[] = [
  { value: 'pendientes', label: 'Pendientes de Acreditar' },
  { value: 'acreditados', label: 'Acreditados' },
  { value: 'anulados', label: 'Anulados' },
  { value: 'todos', label: 'Todos' },
];

function todayIsoDate(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isChequePendiente(cheque: ChequePropio): boolean {
  if (cheque.anulado === 1) return false;
  if (cheque.fechaAcred) return false;
  if (cheque.baja != null && cheque.baja !== 0) return false;
  return true;
}

const ChequesPropiosPendientesAcreditar: React.FC = () => {
  const loadStored = () =>
    loadTesoreriaFiltros<ChequesPropiosPendientesFiltrosStored>(
      TESORERIA_FILTROS_KEYS.chequesPropiosPendientesAcreditar
    );

  const storedEstado = storedString(loadStored(), 'estado', 'pendientes') as ChequePropioEstadoFiltro;
  const estadoInicial: ChequePropioEstadoFiltro = ESTADOS.some((e) => e.value === storedEstado)
    ? storedEstado
    : 'pendientes';

  const [cheques, setCheques] = useState<ChequePropio[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => storedNumber(loadStored(), 'rowsPerPage', 20));
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(() => storedString(loadStored(), 'searchTerm', ''));
  const [estado, setEstado] = useState<ChequePropioEstadoFiltro>(estadoInicial);
  const [fechaVencimientoDesde, setFechaVencimientoDesde] = useState(() =>
    storedString(loadStored(), 'fechaVencimientoDesde', '')
  );
  const [fechaVencimientoHasta, setFechaVencimientoHasta] = useState(() =>
    storedString(loadStored(), 'fechaVencimientoHasta', '')
  );
  const [order, setOrder] = useState<ChequesSortDir>('asc');
  const [orderBy, setOrderBy] = useState<ChequePropioSortKey | ''>('');
  const [fechaAcred, setFechaAcred] = useState(todayIsoDate);
  const [seleccionados, setSeleccionados] = useState<Set<number>>(new Set());
  const [accionLoading, setAccionLoading] = useState(false);
  const [openAnularDialog, setOpenAnularDialog] = useState(false);
  const [motivoAnulacion, setMotivoAnulacion] = useState('');
  const [reloadToken, setReloadToken] = useState(0);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  usePersistTesoreriaFiltros(
    TESORERIA_FILTROS_KEYS.chequesPropiosPendientesAcreditar,
    {
      searchTerm,
      rowsPerPage,
      estado,
      fechaVencimientoDesde,
      fechaVencimientoHasta,
    },
    [searchTerm, rowsPerPage, estado, fechaVencimientoDesde, fechaVencimientoHasta]
  );

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const pageSize = Math.min(rowsPerPage, 20);
      const result = await tesoreriaService.getChequesPropios({
        pageNumber: page + 1,
        pageSize,
        searchTerm: searchTerm.trim() || undefined,
        estado,
        fechaVencimientoDesde: fechaVencimientoDesde || undefined,
        fechaVencimientoHasta: fechaVencimientoHasta || undefined,
        ...(orderBy ? { sortBy: orderBy, sortDir: order } : {}),
      });
      setCheques(result.chequesPropios);
      setTotalCount(result.totalCount);
    } catch (error: any) {
      console.error('Error al obtener los cheques propios:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'No se pudieron cargar los cheques propios.',
        { variant: 'error' }
      );
      setCheques([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    page,
    rowsPerPage,
    searchTerm,
    estado,
    fechaVencimientoDesde,
    fechaVencimientoHasta,
    order,
    orderBy,
    enqueueSnackbar,
    reloadToken,
  ]);

  useEffect(() => {
    const timer = setTimeout(() => {
      void loadData();
    }, searchTerm.trim() ? 500 : 0);
    return () => clearTimeout(timer);
  }, [loadData, searchTerm]);

  const nroCargasPagina = useMemo(() => cheques.map((c) => c.nroCarga), [cheques]);
  const todosPaginaSeleccionados =
    nroCargasPagina.length > 0 && nroCargasPagina.every((id) => seleccionados.has(id));
  const algunoPaginaSeleccionado = nroCargasPagina.some((id) => seleccionados.has(id));

  const chequesSeleccionadosPendientes = useMemo(
    () => cheques.filter((c) => seleccionados.has(c.nroCarga) && isChequePendiente(c)),
    [cheques, seleccionados]
  );

  const puedeAcreditar =
    estado === 'pendientes' &&
    seleccionados.size > 0 &&
    Boolean(fechaAcred) &&
    !accionLoading;

  const puedeAnular =
    (estado === 'pendientes' || estado === 'todos') &&
    seleccionados.size > 0 &&
    !accionLoading;

  const handleToggleCheque = (nroCarga: number, checked: boolean) => {
    setSeleccionados((prev) => {
      const next = new Set(prev);
      if (checked) next.add(nroCarga);
      else next.delete(nroCarga);
      return next;
    });
  };

  const handleMarcarTodo = () => {
    setSeleccionados((prev) => {
      const next = new Set(prev);
      nroCargasPagina.forEach((id) => next.add(id));
      return next;
    });
  };

  const handleDesmarcarTodo = () => {
    setSeleccionados(new Set());
  };

  const handleToggleTodosPagina = () => {
    if (todosPaginaSeleccionados) {
      setSeleccionados((prev) => {
        const next = new Set(prev);
        nroCargasPagina.forEach((id) => next.delete(id));
        return next;
      });
    } else {
      handleMarcarTodo();
    }
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = Math.min(parseInt(event.target.value, 10), 20);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleRequestSort = (property: ChequePropioSortKey) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const handleEstadoChange = (_: React.ChangeEvent<HTMLInputElement>, value: string) => {
    setEstado(value as ChequePropioEstadoFiltro);
    setSeleccionados(new Set());
    setPage(0);
  };

  const handleAcreditar = async () => {
    if (seleccionados.size === 0) {
      enqueueSnackbar('Seleccioná al menos un cheque para acreditar.', { variant: 'warning' });
      return;
    }
    if (!fechaAcred) {
      enqueueSnackbar('Indicá la fecha de acreditación.', { variant: 'warning' });
      return;
    }

    const nroCargas =
      chequesSeleccionadosPendientes.length > 0
        ? chequesSeleccionadosPendientes.map((c) => c.nroCarga)
        : Array.from(seleccionados);

    try {
      setAccionLoading(true);
      await tesoreriaService.acreditarChequesPropios({
        nroCargas,
        fechaAcred,
      });
      enqueueSnackbar(
        `${nroCargas.length} cheque${nroCargas.length !== 1 ? 's' : ''} acreditado${nroCargas.length !== 1 ? 's' : ''} correctamente.`,
        { variant: 'success' }
      );
      setSeleccionados(new Set());
      setReloadToken((t) => t + 1);
    } catch (error: any) {
      const status = error?.response?.status;
      const message =
        error?.response?.data?.message ||
        (status === 404
          ? 'El endpoint de acreditación aún no está disponible en el servidor.'
          : 'No se pudieron acreditar los cheques seleccionados.');
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setAccionLoading(false);
    }
  };

  const handleConfirmAnular = async () => {
    if (seleccionados.size === 0) {
      enqueueSnackbar('Seleccioná al menos un cheque para anular.', { variant: 'warning' });
      return;
    }

    const nroCargas =
      chequesSeleccionadosPendientes.length > 0
        ? chequesSeleccionadosPendientes.map((c) => c.nroCarga)
        : Array.from(seleccionados);

    try {
      setAccionLoading(true);
      await tesoreriaService.anularChequesPropios({
        nroCargas,
        motivo: motivoAnulacion.trim() || null,
      });
      enqueueSnackbar(
        `${nroCargas.length} cheque${nroCargas.length !== 1 ? 's' : ''} anulado${nroCargas.length !== 1 ? 's' : ''} correctamente.`,
        { variant: 'success' }
      );
      setOpenAnularDialog(false);
      setMotivoAnulacion('');
      setSeleccionados(new Set());
      setReloadToken((t) => t + 1);
    } catch (error: any) {
      const status = error?.response?.status;
      const message =
        error?.response?.data?.message ||
        (status === 404
          ? 'El endpoint de anulación aún no está disponible en el servidor.'
          : 'No se pudieron anular los cheques seleccionados.');
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setAccionLoading(false);
    }
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(value);

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (Number.isNaN(date.getTime())) return dateString;
      return new Intl.DateTimeFormat('es-AR', { dateStyle: 'short' }).format(date);
    } catch {
      return dateString;
    }
  };

  const destinoTexto = (cheque: ChequePropio) => {
    const destino = cheque.destino?.trim();
    if (destino) return destino;
    if (cheque.nroDestino) return String(cheque.nroDestino);
    return '-';
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', maxWidth: '100%', overflowX: 'hidden' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 3 }}
          >
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/tesoreria/informes')}
              sx={{ textTransform: 'none' }}
            >
              Informes
            </Button>
            <Typography
              variant="h4"
              component="h1"
              sx={{ fontWeight: 600, color: '#1976d2', wordBreak: 'break-word' }}
            >
              Consulta de Cheques Propios
            </Typography>
          </Stack>

          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <FormLabel component="legend" sx={{ mb: 0.5, color: 'text.secondary' }}>
              Estado
            </FormLabel>
            <RadioGroup
              row={!isMobile}
              value={estado}
              onChange={handleEstadoChange}
              sx={isMobile ? { gap: 0 } : undefined}
            >
              {ESTADOS.map((opt) => (
                <FormControlLabel
                  key={opt.value}
                  value={opt.value}
                  control={<Radio size="small" />}
                  label={opt.label}
                />
              ))}
            </RadioGroup>
          </FormControl>

          <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Buscar por librador, banco, número de cheque..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3} md={2}>
              <TextField
                type="date"
                fullWidth
                size="small"
                label="Desde Vencimiento"
                value={fechaVencimientoDesde}
                onChange={(e) => {
                  setFechaVencimientoDesde(e.target.value);
                  setPage(0);
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={3} md={2}>
              <TextField
                type="date"
                fullWidth
                size="small"
                label="Hasta Vencimiento"
                value={fechaVencimientoHasta}
                onChange={(e) => {
                  setFechaVencimientoHasta(e.target.value);
                  setPage(0);
                }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : isMobile ? (
            <Stack spacing={2}>
              {cheques.length > 0 && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Checkbox
                    indeterminate={algunoPaginaSeleccionado && !todosPaginaSeleccionados}
                    checked={todosPaginaSeleccionados}
                    onChange={handleToggleTodosPagina}
                    disabled={cheques.length === 0}
                    inputProps={{ 'aria-label': 'seleccionar todos los cheques de la página' }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Seleccionar página
                  </Typography>
                </Stack>
              )}
              {cheques.length === 0 ? (
                <Typography align="center" color="text.secondary" sx={{ py: 4 }}>
                  {searchTerm
                    ? 'No se encontraron cheques con los criterios de búsqueda'
                    : 'No hay cheques propios para mostrar'}
                </Typography>
              ) : (
                cheques.map((cheque) => {
                  const seleccionado = seleccionados.has(cheque.nroCarga);
                  return (
                    <Card
                      key={cheque.nroCarga}
                      sx={{
                        borderRadius: 2,
                        border: seleccionado ? '2px solid #1976d2' : '1px solid #e0e0e0',
                        bgcolor: seleccionado ? 'rgba(25, 118, 210, 0.04)' : undefined,
                        '&:hover': {
                          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                        },
                      }}
                    >
                      <CardContent>
                        <Stack spacing={1.5}>
                          <Stack direction="row" alignItems="flex-start" spacing={0.5}>
                            <Box sx={{ mt: -0.5 }}>
                              <Checkbox
                                checked={seleccionado}
                                onChange={(e) => handleToggleCheque(cheque.nroCarga, e.target.checked)}
                                inputProps={{ 'aria-label': `seleccionar cheque ${cheque.nroCarga}` }}
                              />
                            </Box>
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Cheque #{formatNroChequeVista(cheque.nroCheque)}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                {cheque.nombreBanco?.trim() || '-'}
                                {cheque.nroCuenta?.trim() ? ` · Cta ${cheque.nroCuenta.trim()}` : ''}
                              </Typography>
                            </Box>
                          </Stack>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Destino:</strong> {destinoTexto(cheque)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Librador:</strong> {cheque.librador?.trim() || '-'}
                          </Typography>
                          {cheque.concepto?.trim() && (
                            <Typography variant="body2" color="text.secondary">
                              <strong>Concepto:</strong> {cheque.concepto.trim()}
                            </Typography>
                          )}
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1976d2' }}>
                            {formatCurrency(cheque.importe || 0)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            <strong>Venc.:</strong> {formatDate(cheque.fechaVencimiento)}
                            {' · '}
                            <strong>Acred.:</strong> {formatDate(cheque.fechaAcred)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Nº Bco {cheque.nroBanco || '-'} · Nº Suc {cheque.nroSucursal || '-'}
                          </Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })
              )}
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 20]}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${Math.min(to, count)} de ${count !== -1 ? count : `más de ${to}`}`
                }
                sx={{
                  '& .MuiTablePagination-toolbar': {
                    flexWrap: 'wrap',
                    gap: 1,
                    px: 1,
                  },
                }}
              />
            </Stack>
          ) : (
          <TableContainer component={Paper} sx={{ borderRadius: 2, overflowX: 'auto' }}>
              <>
                <Table
                  size="small"
                  sx={{
                    '& .MuiTableCell-root': { px: 1, py: 0.75 },
                    '& .MuiTableCell-paddingCheckbox': { px: 0.5 },
                  }}
                >
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell padding="checkbox" sx={{ width: 40 }}>
                        <Checkbox
                          indeterminate={algunoPaginaSeleccionado && !todosPaginaSeleccionados}
                          checked={todosPaginaSeleccionados}
                          onChange={handleToggleTodosPagina}
                          disabled={cheques.length === 0}
                          inputProps={{ 'aria-label': 'seleccionar todos los cheques de la página' }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }} sortDirection={orderBy === 'nombreBanco' ? order : false}>
                        <TableSortLabel
                          active={orderBy === 'nombreBanco'}
                          direction={orderBy === 'nombreBanco' ? order : 'asc'}
                          onClick={() => handleRequestSort('nombreBanco')}
                        >
                          Banco
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }} sortDirection={orderBy === 'nroCuenta' ? order : false}>
                        <TableSortLabel
                          active={orderBy === 'nroCuenta'}
                          direction={orderBy === 'nroCuenta' ? order : 'asc'}
                          onClick={() => handleRequestSort('nroCuenta')}
                        >
                          Nro. De Cuenta
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }} sortDirection={orderBy === 'destino' ? order : false}>
                        <TableSortLabel
                          active={orderBy === 'destino'}
                          direction={orderBy === 'destino' ? order : 'asc'}
                          onClick={() => handleRequestSort('destino')}
                        >
                          Destino
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }} sortDirection={orderBy === 'librador' ? order : false}>
                        <TableSortLabel
                          active={orderBy === 'librador'}
                          direction={orderBy === 'librador' ? order : 'asc'}
                          onClick={() => handleRequestSort('librador')}
                        >
                          Librador
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Concepto</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right" sortDirection={orderBy === 'nroBanco' ? order : false}>
                        <TableSortLabel
                          active={orderBy === 'nroBanco'}
                          direction={orderBy === 'nroBanco' ? order : 'asc'}
                          onClick={() => handleRequestSort('nroBanco')}
                        >
                          Nº Bco
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right" sortDirection={orderBy === 'nroSucursal' ? order : false}>
                        <TableSortLabel
                          active={orderBy === 'nroSucursal'}
                          direction={orderBy === 'nroSucursal' ? order : 'asc'}
                          onClick={() => handleRequestSort('nroSucursal')}
                        >
                          Nº Suc
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right" sortDirection={orderBy === 'nroCheque' ? order : false}>
                        <TableSortLabel
                          active={orderBy === 'nroCheque'}
                          direction={orderBy === 'nroCheque' ? order : 'asc'}
                          onClick={() => handleRequestSort('nroCheque')}
                        >
                          Nº Cheque
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }} sortDirection={orderBy === 'fechaVencimiento' ? order : false}>
                        <TableSortLabel
                          active={orderBy === 'fechaVencimiento'}
                          direction={orderBy === 'fechaVencimiento' ? order : 'asc'}
                          onClick={() => handleRequestSort('fechaVencimiento')}
                        >
                          Vencimiento
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }} sortDirection={orderBy === 'fechaAcred' ? order : false}>
                        <TableSortLabel
                          active={orderBy === 'fechaAcred'}
                          direction={orderBy === 'fechaAcred' ? order : 'asc'}
                          onClick={() => handleRequestSort('fechaAcred')}
                        >
                          Acreditación
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right" sortDirection={orderBy === 'importe' ? order : false}>
                        <TableSortLabel
                          active={orderBy === 'importe'}
                          direction={orderBy === 'importe' ? order : 'asc'}
                          onClick={() => handleRequestSort('importe')}
                        >
                          Importe
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cheques.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={12} align="center" sx={{ py: 4 }}>
                          <Typography color="text.secondary">
                            {searchTerm
                              ? 'No se encontraron cheques con los criterios de búsqueda'
                              : 'No hay cheques propios para mostrar'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      cheques.map((cheque) => {
                        const seleccionado = seleccionados.has(cheque.nroCarga);
                        return (
                          <TableRow key={cheque.nroCarga} hover selected={seleccionado}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={seleccionado}
                                onChange={(e) => handleToggleCheque(cheque.nroCarga, e.target.checked)}
                                inputProps={{ 'aria-label': `seleccionar cheque ${cheque.nroCarga}` }}
                              />
                            </TableCell>
                            <TableCell>{cheque.nombreBanco?.trim() || '-'}</TableCell>
                            <TableCell>{cheque.nroCuenta?.trim() || '-'}</TableCell>
                            <TableCell>{destinoTexto(cheque)}</TableCell>
                            <TableCell>{cheque.librador?.trim() || '-'}</TableCell>
                            <TableCell>{cheque.concepto?.trim() || '-'}</TableCell>
                            <TableCell align="right">{cheque.nroBanco || '-'}</TableCell>
                            <TableCell align="right">{cheque.nroSucursal || '-'}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>
                              {formatNroChequeVista(cheque.nroCheque)}
                            </TableCell>
                            <TableCell>{formatDate(cheque.fechaVencimiento)}</TableCell>
                            <TableCell>{formatDate(cheque.fechaAcred)}</TableCell>
                            <TableCell align="right">{formatCurrency(cheque.importe || 0)}</TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 20]}
                  labelRowsPerPage="Filas por página:"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${Math.min(to, count)} de ${count !== -1 ? count : `más de ${to}`}`
                  }
                />
              </>
          </TableContainer>
          )}

          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'flex-end' }}
            justifyContent="space-between"
            sx={{ mt: 3 }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              <Button
                variant="outlined"
                startIcon={<CheckBoxIcon />}
                onClick={handleMarcarTodo}
                disabled={cheques.length === 0 || loading}
                fullWidth={isMobile}
                size={isMobile ? 'small' : 'medium'}
              >
                Marcar Todo
              </Button>
              <Button
                variant="outlined"
                color="inherit"
                startIcon={<CheckBoxOutlineBlankIcon />}
                onClick={handleDesmarcarTodo}
                disabled={seleccionados.size === 0}
                fullWidth={isMobile}
                size={isMobile ? 'small' : 'medium'}
              >
                Desmarcar Todo
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<CancelIcon />}
                onClick={() => setOpenAnularDialog(true)}
                disabled={!puedeAnular}
                fullWidth={isMobile}
                size={isMobile ? 'small' : 'medium'}
              >
                Anular
              </Button>
            </Stack>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', sm: 'flex-end' }}
            >
              {seleccionados.size > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                  {seleccionados.size} seleccionado{seleccionados.size !== 1 ? 's' : ''}
                </Typography>
              )}
              <TextField
                type="date"
                size="small"
                label="Fecha Acreditación"
                value={fechaAcred}
                onChange={(e) => setFechaAcred(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth={isMobile}
                sx={{ minWidth: isMobile ? undefined : 180 }}
              />
              <Button
                variant="contained"
                color="primary"
                startIcon={accionLoading ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
                onClick={() => void handleAcreditar()}
                disabled={!puedeAcreditar}
                fullWidth={isMobile}
                size={isMobile ? 'small' : 'medium'}
              >
                Acreditar
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Dialog
        open={openAnularDialog}
        onClose={() => !accionLoading && setOpenAnularDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Anular cheques propios</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            ¿Confirmás anular {seleccionados.size} cheque{seleccionados.size !== 1 ? 's' : ''}{' '}
            seleccionado{seleccionados.size !== 1 ? 's' : ''}?
          </DialogContentText>
          <TextField
            fullWidth
            size="small"
            label="Motivo (opcional)"
            value={motivoAnulacion}
            onChange={(e) => setMotivoAnulacion(e.target.value)}
            disabled={accionLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAnularDialog(false)} disabled={accionLoading}>
            Cancelar
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => void handleConfirmAnular()}
            disabled={accionLoading}
          >
            Anular
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChequesPropiosPendientesAcreditar;
